import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  CreateDocumentTagDto,
  DocumentTagDto,
  DocumentTagsListDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { PaginationOptionsWithSortArray } from '@sib/api/types';

const CACHE_TIME = 5000;

const prependDocumentTagToListCaches = (
  queryClient: QueryClient,
  newTag: DocumentTagDto
) => {
  queryClient.setQueriesData<DocumentTagsListDto>(
    { queryKey: [QueryKey.Resource.DocumentTags] },
    old => {
      if (!old?.items || old.items.some(tag => tag.uuid === newTag.uuid))
        return old;

      return {
        ...old,
        items: [...old.items, newTag],
        totalItems: old.totalItems + 1,
      };
    }
  );
};

export const useDocumentTagsList = (
  organizationId: string,
  query: PaginationOptionsWithSortArray,
  options?: Omit<UseQueryOptions<DocumentTagsListDto>, 'queryKey'>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.DocumentTags, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizationId.documentTagsControllerFindAll(
        organizationId || '',
        query
      ),
  });

export type UseCreateTagOptions = UseMutationOptions<
  DocumentTagDto,
  Error,
  CreateDocumentTagDto
> & {
  organizationId: string;
};

export const useCreateDocumentTag = ({
  organizationId,
  ...options
}: UseCreateTagOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organizationId.documentTagsControllerCreate(organizationId, data),
    onSuccess: (newTag, ...args) => {
      prependDocumentTagToListCaches(queryClient, newTag);
      options?.onSuccess?.(newTag, ...args);
    },
  });
};

export interface DeleteDocumentTagPayload {
  organizationId: string;
  tagId: string;
}

export const useDeleteDocumentTag = (
  options?: UseMutationOptions<unknown, Error, DeleteDocumentTagPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.DocumentTags],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ tagId, organizationId }) =>
      api.organizationId.documentTagsControllerDeleteOne(tagId, organizationId),
  });
};
